import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authMe, authLogin, authRegister, authRegisterWithInvite, authGoogleSession, authLogout, getToken, getStoredUser, registerUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext(null);

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = logged out, object = logged in
  const [authError, setAuthError] = useState("");

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return null;
    }
    try {
      const me = await authMe();
      setUser(me);
      return me;
    } catch (err) {
      if (err.response?.status !== 401) console.error("auth refresh:", err);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback (hash contains session_id),
    // skip the /me check. AuthCallback handles the exchange and sets the token first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // Don't flip user to null mid-callback; AuthCallback will call setSession() when ready.
      registerUnauthorizedHandler(() => setUser(null));
      return;
    }
    const cached = getStoredUser();
    if (cached && getToken()) setUser(cached);
    refresh();
    registerUnauthorizedHandler(() => setUser(null));
    // refresh is stable (useCallback with []); the imported helpers are module-level
    // and have no dependencies of their own — intentionally omitting from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  async function loginWithGoogleSession(sessionId) {
    setAuthError("");
    try {
      const u = await authGoogleSession(sessionId);
      setUser(u);
      return u;
    } catch (err) {
      setAuthError(formatApiError(err.response?.data?.detail) || "Google sign-in failed.");
      throw err;
    }
  }

  async function login(email, password) {
    setAuthError("");
    try {
      const u = await authLogin(email, password);
      setUser(u);
      return u;
    } catch (err) {
      setAuthError(formatApiError(err.response?.data?.detail) || "Login failed.");
      throw err;
    }
  }

  async function register(email, password, inviteToken) {
    setAuthError("");
    try {
      const u = inviteToken
        ? await authRegisterWithInvite(email, password, inviteToken)
        : await authRegister(email, password);
      setUser(u);
      return u;
    } catch (err) {
      setAuthError(formatApiError(err.response?.data?.detail) || "Registration failed.");
      throw err;
    }
  }

  async function logout() {
    await authLogout();
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, authError, login, register, loginWithGoogleSession, logout, refresh, setAuthError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
